import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  getBlogConfig,
  saveBlogConfig,
  getBlogPosts,
  deleteBlogPost,
  updateBlogPost,
} from '../../../services/CommonServices';
import PostEditor from './PostEditor';

const Container = styled.div`
  color: #fff;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #444;
`;

const Title = styled.h6`
  margin: 0;
  color: #eee;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Button = styled.button`
  background-color: ${(props) => {
    if (props.variant === 'danger') return '#dc3545';
    if (props.variant === 'secondary') return '#6c757d';
    return '#28a745';
  }};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => {
      if (props.variant === 'danger') return '#bd2130';
      if (props.variant === 'secondary') return '#5a6268';
      return '#218838';
    }};
  }
`;

const PostList = styled.div`
  margin-top: 20px;
`;

const PostItem = styled.div`
  background-color: ${props => props.isSelected ? '#3a3a3a' : '#333'};
  border-radius: 4px;
  margin-bottom: 8px;
  overflow: hidden;
  border: 1px solid ${props => props.isSelected ? '#555' : 'transparent'};
  transition: all 0.2s ease;
`;

const PostHeader = styled.div`
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #3d3d3d;
  }
`;

const PostTitle = styled.span`
  font-weight: 500;
  color: #fff;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#28a745' : '#6c757d'};
  flex-shrink: 0;
`;

const ExpandIcon = styled.span`
  color: #888;
  font-size: 0.8rem;
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const PostActions = styled.div`
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-top: 1px solid #444;
  animation: slideDown 0.2s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #555;
    transition: 0.3s;
    border-radius: 20px;

    &:before {
      position: absolute;
      content: "";
      height: 14px;
      width: 14px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }

  input:checked + span {
    background-color: #28a745;
  }

  input:checked + span:before {
    transform: translateX(16px);
  }
`;

const SwitchText = styled.span`
  font-size: 0.8rem;
  color: ${props => props.active ? '#28a745' : '#888'};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const SmallButton = styled.button`
  background-color: ${props => {
    if (props.variant === 'danger') return '#dc3545';
    if (props.variant === 'secondary') return '#555';
    return '#007bff';
  }};
  color: white;
  border: none;
  border-radius: 3px;
  padding: 4px 10px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => {
      if (props.variant === 'danger') return '#bd2130';
      if (props.variant === 'secondary') return '#444';
      return '#0056b3';
    }};
  }
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  color: #888;
  flex-shrink: 0;
`;

const PostDate = styled.span`
  color: #888;
`;

const ScheduleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #f0ad4e33;
  color: #f0ad4e;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #444;
`;

const PageButton = styled.button`
  background-color: ${props => props.active ? '#007bff' : '#333'};
  color: white;
  border: 1px solid ${props => props.active ? '#007bff' : '#555'};
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: ${props => props.active ? '#0056b3' : '#444'};
    border-color: ${props => props.active ? '#0056b3' : '#666'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  color: #888;
  font-size: 0.85rem;
`;

const BlogManager = ({ siteId }) => {
  const [config, setConfig] = useState({ enabled: false, title: 'Blog' });
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState('list'); // 'list' | 'editor'
  const [editingPost, setEditingPost] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const POSTS_PER_PAGE = 10;

  useEffect(() => {
    if (siteId) {
      loadConfig();
      loadPosts(1);
    }
  }, [siteId]);

  const loadConfig = async () => {
    try {
      const res = await getBlogConfig(siteId);
      if (res.data.success) setConfig(res.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadPosts = async (page = 1) => {
    try {
      const res = await getBlogPosts(siteId, { limit: POSTS_PER_PAGE, page });
      if (res.data.success) {
        setPosts(res.data.data.posts);
        setPagination({
          page: res.data.data.pagination.page,
          totalPages: res.data.data.pagination.totalPages,
          total: res.data.data.pagination.total,
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Format date to readable string
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // Format datetime for schedule badge
  const formatScheduleDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Check if scheduled post is still pending
  const isSchedulePending = (scheduledAt) => {
    if (!scheduledAt) return false;
    return new Date(scheduledAt) > new Date();
  };

  const handleConfigSave = async () => {
    try {
      const res = await saveBlogConfig(siteId, config);
      if (res.data.success) alert('Settings saved');
    } catch (err) {
      alert('Error saving settings');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await deleteBlogPost(id);
      setSelectedPostId(null);
      loadPosts(pagination.page);
    } catch (err) {
      alert('Error deleting post');
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await updateBlogPost(post.id, {
        ...post,
        isPublished: !post.is_published,
        scheduledAt: post.scheduled_at || null,
      });
      loadPosts(pagination.page);
    } catch (err) {
      alert('Error updating post');
    }
  };

  const handlePostClick = (postId) => {
    setSelectedPostId(selectedPostId === postId ? null : postId);
  };

  if (view === 'editor') {
    return (
      <PostEditor
        siteId={siteId}
        post={editingPost}
        onCancel={() => {
          setView('list');
          setEditingPost(null);
        }}
        onSave={() => {
          setView('list');
          setEditingPost(null);
          loadPosts(pagination.page);
        }}
      />
    );
  }

  return (
    <Container>
      <Header>
        <Title>Blog Settings</Title>
      </Header>

      <div className="mb-3">
        <div className="form-check form-switch mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="blogEnabled"
            checked={config.enabled}
            onChange={(e) =>
              setConfig({ ...config, enabled: e.target.checked })
            }
          />
          <label className="form-check-label" htmlFor="blogEnabled">
            Enable Blog
          </label>
        </div>

        <label className="form-label text-secondary small">Blog Title</label>
        <div className="d-flex gap-2">
          <input
            type="text"
            className="form-control form-control-sm bg-dark text-white border-secondary"
            value={config.title}
            onChange={(e) => setConfig({ ...config, title: e.target.value })}
          />
          <Button onClick={handleConfigSave}>Save</Button>
        </div>
      </div>

      <Header style={{ marginTop: '30px' }}>
        <Title>Posts</Title>
        <Button onClick={() => setView('editor')}>New Post</Button>
      </Header>

      <PostList>
        {posts.length === 0 ? (
          <p className="text-muted text-center small">No posts yet.</p>
        ) : (
          posts.map((post) => {
            const isSelected = selectedPostId === post.id;
            const isPending = isSchedulePending(post.scheduled_at);
            return (
              <PostItem key={post.id} isSelected={isSelected}>
                <PostHeader onClick={() => handlePostClick(post.id)}>
                  <StatusDot active={post.is_published && !isPending} />
                  <PostTitle>{post.title}</PostTitle>
                  <PostMeta>
                    {post.is_published && isPending && (
                      <ScheduleBadge title={`Scheduled: ${formatScheduleDate(post.scheduled_at)}`}>
                        Scheduled {formatScheduleDate(post.scheduled_at)}
                      </ScheduleBadge>
                    )}
                    <PostDate>{formatDate(post.created_at)}</PostDate>
                  </PostMeta>
                  <ExpandIcon isOpen={isSelected}>▼</ExpandIcon>
                </PostHeader>
                {isSelected && (
                  <PostActions>
                    <ActionRow>
                      <SwitchWrapper>
                        <Switch>
                          <input
                            type="checkbox"
                            checked={post.is_published}
                            onChange={() => handleTogglePublish(post)}
                          />
                          <span />
                        </Switch>
                        <SwitchText active={post.is_published}>
                          {post.is_published ? 'Published' : 'Draft'}
                        </SwitchText>
                      </SwitchWrapper>
                      <ButtonGroup>
                        <SmallButton
                          onClick={() => {
                            setEditingPost(post);
                            setView('editor');
                          }}
                        >
                          Edit
                        </SmallButton>
                        <SmallButton
                          variant="danger"
                          onClick={() => handleDelete(post.id)}
                        >
                          Del
                        </SmallButton>
                      </ButtonGroup>
                    </ActionRow>
                  </PostActions>
                )}
              </PostItem>
            );
          })
        )}
      </PostList>

      {pagination.totalPages > 1 && (
        <PaginationWrapper>
          <PageButton
            onClick={() => loadPosts(pagination.page - 1)}
            disabled={pagination.page <= 1}
          >
            Prev
          </PageButton>
          <PageInfo>
            {pagination.page} / {pagination.totalPages}
          </PageInfo>
          <PageButton
            onClick={() => loadPosts(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
          >
            Next
          </PageButton>
        </PaginationWrapper>
      )}
    </Container>
  );
};

export default BlogManager;

